import clsx from 'clsx';
import { PIPELINE_STAGES, type OnboardingStatus } from '../../api/client';

const STAGE_LABELS: Record<string, string> = {
  queued:        'Queued',
  sourcing:      'Sourcing',
  extracting:    'Extracting',
  review_specs:  'Review Specs',
  composing:     'Composing',
  review_labels: 'Review Labels',
  provisioning:  'Provisioning',
  completed:     'Complete',
};

const REVIEW_STAGES = new Set(['review_specs', 'review_labels']);

interface OnboardingStepperProps {
  status: OnboardingStatus;
  failedStage?: string | null;
}

export function OnboardingStepper({ status, failedStage }: OnboardingStepperProps) {
  const currentIdx = PIPELINE_STAGES.indexOf(status);
  // For failed/cancelled, show the failed stage position
  const failedIdx = (status === 'failed' && failedStage)
    ? PIPELINE_STAGES.indexOf(failedStage as OnboardingStatus)
    : -1;

  return (
    <div className="w-full overflow-x-auto">
      <div className="flex items-center min-w-[600px]">
        {PIPELINE_STAGES.map((stage, idx) => {
          const isCompleted = status === 'completed'
            ? true
            : (status !== 'failed' && status !== 'cancelled' && idx < currentIdx);
          const isCurrent = (!['failed', 'cancelled'].includes(status)) && idx === currentIdx;
          const isFailed = status === 'failed' && idx === failedIdx;
          const isPending = !isCompleted && !isCurrent && !isFailed;
          const isReview = REVIEW_STAGES.has(stage);

          return (
            <div key={stage} className="flex items-center flex-1">
              {/* Connector line */}
              {idx > 0 && (
                <div className={clsx(
                  'h-0.5 flex-1 -mx-1',
                  isCompleted || isCurrent ? 'bg-green-400' : isFailed ? 'bg-red-400' : 'bg-gray-200',
                )} />
              )}

              {/* Stage circle + label */}
              <div className="flex flex-col items-center relative">
                <div className={clsx(
                  'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all',
                  isCompleted && 'bg-green-500 border-green-500 text-white',
                  isCurrent && 'bg-blue-500 border-blue-500 text-white animate-pulse',
                  isFailed && 'bg-red-500 border-red-500 text-white',
                  isPending && 'bg-white border-gray-300 text-gray-400',
                )}>
                  {isCompleted ? '✓' : isFailed ? '✗' : isReview ? '★' : idx + 1}
                </div>
                <span className={clsx(
                  'text-[10px] mt-1 whitespace-nowrap font-medium',
                  isCompleted && 'text-green-700',
                  isCurrent && 'text-blue-700',
                  isFailed && 'text-red-700',
                  isPending && 'text-gray-400',
                )}>
                  {STAGE_LABELS[stage] ?? stage}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
